import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { getTabScrollVisibility } from '../../shared/utils/tab-scroll.util';

export interface TabItem {
  name: string;
  icon?: string;
}

/**
 * Responsive tab view component that renders a horizontal list of tabs with scroll controls and an optional statistics toggle.
 * @component
 * @selector app-tab-view
 * @example
 *   <app-tab-view
 *     [tabs]="tabs"
 *     [defaultActiveTab]="'overview'"
 *     [hideStats]="false"
 *     [hideStatsLabel]="false"
 *     (tabChange)="onTabChange($event)"
 *     (hideStatsChange)="onHideStatsChange($event)">
 *   </app-tab-view>
 *
 * Purpose:
 *   Renders a horizontally scrollable tab menu, manages the active tab state, exposes events when the active tab or stats visibility change,
 *   and shows left/right scroll affordances when the tab list overflows.
 *
 * Key methods:
 *   - checkScroll(): Determines whether left/right scroll buttons should be shown using getTabScrollVisibility and updates showLeftScroll/showRightScroll.
 *   - toggleHideStats(): Toggles the hideStats flag and emits the hideStatsChange event.
 *   - setActiveTab(tabName: string): Sets the active tab, emits tabChange, and rechecks scroll visibility.
 *   - scrollLeft()/scrollRight(): Smoothly scroll the tab menu left or right and re-evaluate scroll visibility after the animation.
 *
 * Important lifecycle hooks and listeners:
 *   - ngOnInit(): Initializes activeTab from defaultActiveTab or the first tab in tabs.
 *   - ngAfterViewInit(): Calls checkScroll() once view children are available.
 *   - @HostListener('window:resize', ['$event']): onResize() calls checkScroll() to update scroll controls on window resize.
 *
 * @input TabItem[] tabs - Array of tab items to render (each TabItem is expected to include at least a name and display label).
 * @input string defaultActiveTab - Optional name of the tab to set active on initialization.
 * @input boolean hideStats - Controls whether statistics are hidden; can be toggled via toggleHideStats and is emitted via hideStatsChange.
 * @input boolean hideStatsLabel - When true, hides the label associated with the stats toggle (presentation-only).
 * @output EventEmitter<string> tabChange - Emitted when the active tab changes; payload is the new tab name.
 * @output EventEmitter<boolean> hideStatsChange - Emitted when hideStats is toggled; payload is the new boolean value.
 */
@Component({
  selector: 'app-tab-view',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tab-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tab-view.component.scss'],
})
export class TabViewComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() tabs: TabItem[] = [];
  @Input() defaultActiveTab?: string;
  @Output() tabChange = new EventEmitter<string>();
  @Input() hideStats = false;
  @Input() hideStatsLabel = false;
  @Output() hideStatsChange = new EventEmitter<boolean>();

  @ViewChild('tabContainer') tabContainer!: ElementRef;
  @ViewChild('tabMenu') tabMenu!: ElementRef;

  showLeftScroll = false;
  showRightScroll = false;
  activeTab = '';

  ngOnInit() {
    this.syncActiveTab();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultActiveTab']) {
      this.syncActiveTab();
    }
  }

  ngAfterViewInit() {
    this.checkScroll();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScroll();
  }

  private syncActiveTab(): void {
    if (this.defaultActiveTab) {
      this.activeTab = this.defaultActiveTab;
    } else if (this.tabs.length > 0) {
      this.activeTab = this.tabs[0].name;
    }
  }

  checkScroll() {
    setTimeout(() => {
      const menu = this.tabMenu?.nativeElement;
      if (!menu) return;
      const { showLeftScroll, showRightScroll } = getTabScrollVisibility(menu);
      this.showLeftScroll = showLeftScroll;
      this.showRightScroll = showRightScroll;
    }, 0);
  }
  toggleHideStats() {
    this.hideStats = !this.hideStats;
    this.hideStatsChange.emit(this.hideStats);
  }
  setActiveTab(tabName: string) {
    this.activeTab = tabName;
    this.tabChange.emit(tabName);
    this.checkScroll();
  }

  scrollLeft() {
    this.tabMenu.nativeElement.scrollBy({ left: -200, behavior: 'smooth' });
    setTimeout(() => this.checkScroll(), 300);
  }

  scrollRight() {
    this.tabMenu.nativeElement.scrollBy({ left: 200, behavior: 'smooth' });
    setTimeout(() => this.checkScroll(), 300);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
